import { Link } from "gatsby"
import React from "react"
import styled from "styled-components"
import { GoToButton } from "styleguide/components"
import {
  FooterIconShakacode,
  IconSlack,
  IconGithub,
  IconLinkedin,
  IconTwitter,
} from "styleguide/icons"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import media from "styleguide/styles/media-queries"
import { Container } from "./grid"
import { H6 } from "./Text"

const FooterRow = styled.div`
  margin-top: 64px;
  display: flex;

  ${media.tablet`
    flex-wrap: wrap;
  `}

  a {
    font-weight: 400;
  }
`

const CopyrightSocialRow = styled.div`
  padding: 30px 0 50px 0;
  font-size: 14px;
  color: ${uiColorToBaseColor("gray")};
  display: flex;

  & > * {
    flex: 0 0 50%;

    ${media.tablet`
      flex: 0 0 100%;
      max-width: 100%;
      margin-bottom: 15px;
    `}
  }

  ${media.tablet`
    flex-wrap: wrap;
  `}
`

const ColumnHeader = styled(H6)`
  margin-top: 0;
  margin-bottom: 10px;
  color: ${uiColorToBaseColor("gray")};
  font-weight: 400;
`

const InfoCol = styled.div`
  flex: 0 0 34%;
  max-width: 34%;
  font-size: 14px;
  color: ${uiColorToBaseColor("gray")};

  ${media.tablet`
    order: 100;
    flex: 0 0 100%;
    max-width: 100%;
  `}
`

const Address = styled.address`
  font-style: normal;
  margin: 15px 0 13px 0;
`

const Phone = styled.a`
  color: #b11100;
  text-decoration: none;
`

const MenuCol = styled.div`
  flex: 0 0 18%;
  max-width: 18%;

  &:last-child {
    flex: 0 0 auto;
  }

  ${media.tablet`
    margin-bottom: 30px;
    flex: 0 0 50%;
    max-width: 50%;
  `}
`

const SocialCol = styled.div`
  text-align: right;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;

  svg {
    margin-left: 16px;
    margin-right: 0;
    width: 32px;
    height: 32px;
  }

  ${media.tablet`
      justify-content: flex-start;
    `}
`

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const Item = styled.li`
  margin-bottom: 3px;

  a {
    color: ${uiColorToBaseColor("gray")};
    text-decoration: none;
    font-size: 14px;

    &:hover {
      text-decoration: underline;
    }
  }
`
const StyledLink = styled(Link)`
  color: ${uiColorToBaseColor("gray")};
`

const StyledIconSlack = styled(IconSlack)`
  width: 16px !important;
  height: 16px !important;
  margin-right: 8px !important;
  margin-left: 0 !important;
`

const StyledGoToButton = styled(GoToButton)`
  padding: 8px 16px 8px 12px;
  width: 137px;
  min-width: fit-content;
  font-size: 13px;
  line-height: 16px;
  color: ${uiColorToBaseColor("black")};
  border: 1px solid ${uiColorToBaseColor("lighterGray")};

  &:hover {
    color: ${uiColorToBaseColor("black")};
    border: 1px solid ${uiColorToBaseColor("lighterGray")};
  }
`

const Footer = () => (
  <footer>
    <Container>
      <FooterRow>
        <InfoCol>
          <Link to="/">
            <FooterIconShakacode />
          </Link>
          <Address>
            ShakaCode, LLC
            <br />
            141 Makahiki Street
            <br />
            Paia, HI 96779
          </Address>
          <Phone href="tel:808-281-7272">808-281-7272</Phone>
        </InfoCol>
        <MenuCol>
          <ColumnHeader>
            <StyledLink to="/about/">About</StyledLink>
          </ColumnHeader>
          <List>
            <Item>
              <Link to="/about/#why">Why us</Link>
            </Item>
            <Item>
              <Link to="/about/#our-values">Our values</Link>
            </Item>
            <Item>
              <Link to="/about/#team">Meet the team</Link>
            </Item>
            <Item>
              <Link to="/career/">Join the team</Link>
            </Item>
          </List>
        </MenuCol>
        <MenuCol>
          <ColumnHeader>
            <StyledLink to="/services/">Services</StyledLink>
          </ColumnHeader>
          <List>
            <Item>
              <Link to="/services/#what-we-do">What we do</Link>
            </Item>
            <Item>
              <Link to="/design/">Design</Link>
            </Item>
            <Item>
              <Link to="/react-on-rails-pro/">React on Rails Pro</Link>
            </Item>
            <Item>
              <Link to="/services/web-scraping-and-data-analysis">
                Web Scraping & Data Analysis
              </Link>
            </Item>
            <Item>
              <Link to="/services/ai-consulting">
                AI Consulting
              </Link>
            </Item>
            <Item>
              <Link to="/open-source-projects/">Open source projects</Link>
            </Item>
          </List>
        </MenuCol>
        <MenuCol>
          <ColumnHeader>Portfolio</ColumnHeader>
          <List>
            <Item>
              <Link to="/recent-work/">Recent work</Link>
            </Item>
            <Item>
              <Link to="/hichee/">HiChee.com</Link>
            </Item>
          </List>
        </MenuCol>
        <MenuCol>
          <ColumnHeader>Insights</ColumnHeader>
          <List>
            <Item>
              <Link to="/blog/">Blog</Link>
            </Item>
            <Item>
              <a
                href="https://forum.shakacode.com"
                target="_blank"
                rel="noopener"
              >
                Forum
              </a>
            </Item>
            <Item>
              <Link to="/talks/">Talks</Link>
            </Item>
          </List>
        </MenuCol>
        <MenuCol>
          <ColumnHeader>Docs</ColumnHeader>
          <List>
            <Item>
              <Link to="/react-on-rails/docs/">React on Rails</Link>
            </Item>
            <Item>
              <Link to="/react-on-rails-pro/docs/">
                React on Rails Pro
              </Link>
            </Item>
            <Item>
              <Link to="/control-plane-flow/docs/">
                Control Plane flow
              </Link>
            </Item>
          </List>
        </MenuCol>
      </FooterRow>
      <CopyrightSocialRow>
        <div>Copyright {new Date().getFullYear()} ShakaCode</div>
        <SocialCol>
          <StyledGoToButton
            size="XL"
            href="https://join.slack.com/t/reactrails/shared_invite/enQtNjY3NTczMjczNzYxLTlmYjdiZmY3MTVlMzU2YWE0OWM0MzNiZDI0MzdkZGFiZTFkYTFkOGVjODBmOWEyYWQ3MzA2NGE1YWJjNmVlMGE"
            asExternalLink
            icon={<StyledIconSlack />}
            isIconOnLeft
            target="_blank"
            rel="noopener"
          >
            Join our Slack
          </StyledGoToButton>
          <a href="https://github.com/shakacode" target="_blank" rel="noopener">
            <IconGithub />
          </a>
          <a
            href="https://linkedin.com/company/shakacode"
            target="_blank"
            rel="noopener"
          >
            <IconLinkedin />
          </a>
          <a
            href="https://twitter.com/shakacode"
            target="_blank"
            rel="noopener"
          >
            <IconTwitter />
          </a>
        </SocialCol>
      </CopyrightSocialRow>
    </Container>
  </footer>
)

export default Footer
